import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

import type { PreviewCopy } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { useStudio } from "../app/store";
import { PhysicsThread } from "../components/PhysicsThread";
import { SlidePreview } from "../components/SlidePreview";

const TITLE_LIMIT = 72;
const BODY = "One clear argument, enough evidence to trust it, and no slide pretending to be a storage unit.";
const PREVIEW_FONTS = {
  display: '"Fraunces", Georgia, serif',
  body: '"DM Sans", system-ui, sans-serif',
  utility: '"IBM Plex Mono", monospace',
};

export const Home = () => {
  const { dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const [title, setTitle] = useState("Put the answer on the slide.");
  const artRef = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);

  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  const moveLight = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const element = artRef.current;
    if (!element) return;
    const bounds = element.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      element.style.setProperty("--light-x", `${(x * 100).toFixed(1)}%`);
      element.style.setProperty("--light-y", `${(y * 100).toFixed(1)}%`);
      element.style.setProperty("--art-rx", `${((0.5 - y) * 2.3).toFixed(2)}deg`);
      element.style.setProperty("--art-ry", `${((x - 0.5) * 2.9).toFixed(2)}deg`);
    });
  };

  const start = (previewCopy?: PreviewCopy) => {
    dispatch({ type: "enter", entry: "type" });
    if (previewCopy) dispatch({ type: "setPreviewCopy", previewCopy });
    announce("Opening Type Set. First, choose how deep we should go.");
    navigate("/studio");
  };

  return (
    <div className="home-shell standalone-home standalone-home--type">
      <section className="hero-v2 product-hero" aria-labelledby="home-title">
        <div className="hero-v2__copy">
          <p className="eyebrow">A PITCH.DOG TOOL FOR DECKS WITH SOMETHING TO SAY</p>
          <h1 id="home-title">A deck should<br /><i>sound like somebody.</i></h1>
          <p className="home-standfirst">Type Set finds a typographic direction that fits the job, the room, and the amount of chaos in the handover. Sixty-four real open-source families. Five honest directions. No font cosplay.</p>

          <div className="teaser-v2 type-teaser">
            <label className="field teaser-v2__field">
              <span>Try the line everybody will remember</span>
              <input className="text-input text-input--large" type="text" maxLength={TITLE_LIMIT} value={title} onChange={(event) => setTitle(event.target.value)} />
              <span className="field-count">{title.length}/{TITLE_LIMIT}</span>
            </label>
            <div className="hero-actions">
              <button type="button" className="btn btn--primary btn--large" onClick={() => start({ slideTitle: title || "Put the answer on the slide.", body: BODY })}>Find my type direction <span aria-hidden="true">→</span></button>
              <button type="button" className="text-link" onClick={() => navigate("/collection")}>Browse all 64 families</button>
            </div>
            <p className="time-note">About 90 seconds. Or take the properly nosy route when the details matter.</p>
          </div>
        </div>

        <div ref={artRef} className="hero-v2__art product-hero__art" onPointerMove={moveLight} onPointerLeave={() => {
          artRef.current?.style.setProperty("--light-x", "62%");
          artRef.current?.style.setProperty("--light-y", "42%");
          artRef.current?.style.setProperty("--art-rx", "0deg");
          artRef.current?.style.setProperty("--art-ry", "0deg");
        }}>
          <picture className="optical-master" aria-hidden="true">
            <source srcSet="./assets/letters-light-optical-still-v1-960w.webp 960w, ./assets/letters-light-optical-still-v1-1600w.webp 1600w" type="image/webp" />
            <img src="./assets/letters-light-optical-still-v1.png" alt="" />
          </picture>
          <span className="hero-v2__light" aria-hidden="true" />
          <PhysicsThread />
          <div className="hero-v2__slide">
            <SlidePreview kicker="THE DECK HAS A VOICE" title={title || "Put the answer on the slide."} body={BODY} fonts={PREVIEW_FONTS} variant="titleBody" label="Live 16:9 type preview" />
          </div>
          <p className="hero-v2__note"><span>PULL THE THREAD</span><small>The type should move you. The thread merely takes that literally.</small></p>
        </div>
      </section>

      <section className="product-promise" aria-labelledby="promise-title">
        <header>
          <p className="eyebrow">NOT A FONT PAIRING SLOT MACHINE</p>
          <h2 id="promise-title">The right voice has to survive the actual deck.</h2>
        </header>
        <div className="promise-grid">
          <article><span>01</span><strong>Answer what can change the result.</strong><p>Two short pages by default. More detail only when you consent to the longer ride.</p></article>
          <article><span>02</span><strong>Meet five distinct directions.</strong><p>Real letterforms, rendered from the exact local files, with attribution attached.</p></article>
          <article><span>03</span><strong>Try to break them.</strong><p>Your words. Five 16:9 slide jobs. Bounded copy. Useful caveats. Then export.</p></article>
        </div>
      </section>

      <section className="type-parade" aria-label="Three real font voices">
        <article style={{ fontFamily: '"Fraunces", Georgia, serif' }}><small>EDITORIAL, NOT PRECIOUS</small><strong>Make the point impossible to miss.</strong><span>Fraunces · Undercase Type</span></article>
        <article style={{ fontFamily: '"Bricolage Grotesque", system-ui, sans-serif' }}><small>CHARACTER WITH A JOB</small><strong>Useful can still have a pulse.</strong><span>Bricolage Grotesque · Mathieu Triay</span></article>
        <article style={{ fontFamily: '"DM Sans", system-ui, sans-serif' }}><small>THE READING WORK</small><strong>Clarity without the beige little cardigan.</strong><span>DM Sans · Colophon Foundry</span></article>
      </section>

      <section className="manifesto-strip manifesto-strip--type" aria-label="Type Set in one sentence">
        <p><span>Find the voice.</span><span>Then make it earn the room.</span></p>
        <small>The tool recommends. The deck still needs a human with taste and a willingness to delete slide 47.</small>
      </section>

      <section className="closing-invite">
        <p className="eyebrow">YOUR DECK IS ALREADY SAYING SOMETHING</p>
        <h2>Let us make sure it means to.</h2>
        <button type="button" className="btn btn--primary btn--large" onClick={() => start()}>
          Start Type Set <span aria-hidden="true">→</span>
        </button>
      </section>
    </div>
  );
};
